<template>
    <v-main class="tenant-login">
        <div class="content">
            <div
                v-if="!$vuetify.breakpoint.smAndDown"
                class="bg-graphics"
                :style="bgStyle"
            ></div>

            <select
                v-if="isMobile"
                class="country"
                @change="onCountryChange"
            >
                <option value="" disabled :selected="!['me', 'sr', 'ba'].includes(locale)">{{ $t('general.country') }}</option>
                <option value="sr" :selected="locale === 'sr'">{{ $t('locales.sr') }}</option>
                <option value="me" :selected="locale === 'me'">{{ $t('locales.me') }}</option>
                <option value="ba" :selected="locale === 'ba'">{{ $t('locales.ba') }}</option>
            </select>

            <v-form class="form">
                <div class="logo" @click="logoClick">
                    <img v-if="isAllegra" src="../../assets/img/allegra/logoDarkHor.svg" alt="" />
                    <img v-else src="../../assets/img/aria/logoDarkVer.svg" alt="" />
                </div>

                <div class="form-wrapper">
                    <h3 class="mb-1">{{ $t('general.login') }}</h3>

                    <p class="text-subtitle-2 grey--text text--darken-2 mb-5">
                        {{ !isDemo ? $t('get_started.login_intro') : $t('get_started.login_intro_demo') }}
                    </p>

                    <v-text-field
                        v-model.trim="form.email"
                        :error-messages="form.$errors.email"
                        :label="$t('general.email')"
                        autofocus
                        validate-on-blur
                        class="mt-5"
                        outlined
                        dark
                        @blur="form.validate('email')"
                        @input="delete form.$errors.email"
                    />

                    <v-text-field
                        v-model="form.password"
                        :error-messages="form.$errors.password"
                        :append-icon="showPassword ? 'fal fa-eye' : 'fal fa-eye-slash'"
                        :type="showPassword ? 'text' : 'password'"
                        :label="$t('general.password')"
                        counter
                        outlined
                        dark
                        @blur="form.validate('password')"
                        @input="delete form.$errors.password"
                        @click:append="showPassword = !showPassword"
                        @keyup.enter.native="login"
                    />

                    <v-btn
                        :disabled="form.$busy"
                        :loading="form.$busy"
                        color="primary"
                        depressed
                        block
                        @click="login"
                    >
                        {{ $t('general.log_in') }}
                    </v-btn>

                    <div class="d-flex mt-5">
                        <v-spacer />
                        <a href="#" @click.prevent="$router.push({ name: 'tenantRegister' }).catch(err => {})">
                            {{ $t('general.dont_have_an_account') }}
                        </a>
                    </div>
                </div>
            </v-form>

            <div v-if="version" class="version-label">
                <div>{{ locale === 'sr' ? 'CodeUX doo' : 'OllieTech doo' }} / {{ isAllegra ? 'Allegra' : 'Aria' }} POS Version {{ version }}</div>
                <div v-if="environmentLabel">{{ environmentLabel }}</div>
            </div>
        </div>
    </v-main>
</template>

<script>
import TenantService from '@/services/TenantService';
import UserService from '@/services/UserService';
import Locale from '@/wrappers/Locale';
import { required, email, minLength } from 'vuelidate/lib/validators';

const packageJson = require('/package.json');

export default {
    created() {
        TenantService.removeToken();
        TenantService.removeTenant();

        UserService.removeToken();
        UserService.removeUser();

        this.isMobile = this.device.platform === 'android' || this.device.platform === 'ios'
    },

    data() {
        return {
            form: new Form(this, 'form', {
                email: null,
                password: null,
                app: this.isAllegra ? 'allegra' : 'aria'
            }),

            showPassword: false,
            isDemo: process.env.VUE_APP_ENV === 'staging',

            logoClickCount: 0,
            logoClickTimeout: null,
            isMobile: false
        };
    },

    validations() {
        return {
            form: {
                email: { required, email },
                password: { required, minLength: minLength(8) },
            }
        }
    },

    methods: {
        /**
         * Login Tenant
         */
        async login() {
            if (!this.form.validate()) return;

            TenantService.login(this.form).then(response => {
                // Redirect to getting started if it hasn't been completed yet.
                if (response.data.tenant.getstarted_status !== 'completed') {
                    this.$router.push({ name: 'gettingStarted' }).catch(err => {});
                }

                // Go to User Login
                this.$router.push({ name: 'userLogin', query: { initial: true } }).catch(err => {});
            }).catch(error => {
                if(error.message === 'sync_exception') {
                    this.$snackbar(this.$t('general.error_contact_support'), 'error', { timeout: 10000 });
                }
            });
        },

        logoClick() {
            this.logoClickCount++;
            if(this.logoClickCount === 10) {
                this.$snackbar(packageJson.version, 'info', { timeout: 10000 });
                this.logoClickCount = 0
                clearTimeout(this.logoClickTimeout)
                return
            }

            clearTimeout(this.logoClickTimeout)
            this.logoClickTimeout = setTimeout(() => {
                this.logoClickCount = 0
            }, 5000)
        },

        async onCountryChange(event) {
            await Locale.setLocale(event.target.value)
            Locale.setLanguage(event.target.value)
        }
    },

    computed: {
        version() {
            return packageJson
                ? ((isAllegra ? '793/' : '794/') + packageJson.certifiedVersion)
                : '';
        },

        environmentLabel() {
            if(process.env.VUE_APP_ENV !== 'production' && process.env.NODE_ENV !== 'production') {
                return process.env.VUE_APP_ENV + ' / ' + process.env.NODE_ENV
            }

            return ''
        },

        bgStyle() {
            return { backgroundImage: `url('/img/${this.isAllegra ? 'allegra' : 'aria'}/background.png')` }
        }
    }
};
</script>

<style lang="scss" scoped>
.tenant-login {
    height: 100%;

    .content {
        height: 100%;
        display: flex;
        justify-content: center;
        background-color: #1f212b;
        color: white;
        position: relative;

        .country {
            position: absolute;
            top: 12px;
            right: 36px;
            color: #bcbdc0;
            text-align: right;
            outline: none;
            cursor: pointer;
        }

        .form {
            display: flex;
            flex-direction: column;
            padding: 44px 36px;
            flex: 3;
            max-width: 600px;
            justify-content: center;

            .logo {
                display: flex;
                justify-content: center;
                width: 100%;
                max-height: 120px;
                margin-top: 20px;
                margin-bottom: 20px;

                img {
                    max-height: 100%;
                    max-width: 250px;
                }
            }

            .form-wrapper {
                display: flex;
                flex-direction: column;
            }
        }

        .bg-graphics {
            flex: 7;
            background-size: cover;
            background-position: bottom left;
            background-color: #101219;
        }

        .version-label {
            font-size: 12px;
            position: absolute;
            bottom: 10px;
            right: 10px;
            color: #646b79;
            text-align: right;
        }
    }
}
</style>
